import { EQUIPMENT_PROCESS_TYPES, normalizeEquipmentProcessType } from './equipmentConstants';
import type { EquipmentProcessType } from './equipmentTypes';

/**
 * Stockers can buffer FOUPs for several processes at once, so their process
 * type is stored as a list rather than a single value:
 *
 *   - `undefined` — never configured; fall back to the legacy single-value field
 *     (`equipmentType` on overlay instances, `customProperties.processType` on
 *     layout elements).
 *   - `null` or `[]` — explicitly "None": the stocker serves no process.
 *
 * The legacy single-value field is kept in sync with the first entry so the
 * simulation request builders and 3D layers keep working unchanged.
 */

function asProcessType(value: unknown): EquipmentProcessType | null {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase();
  if (!raw) return null;
  return normalizeEquipmentProcessType(raw);
}

/** Canonical ordering keeps the stored list stable regardless of click order. */
function inCanonicalOrder(types: Iterable<EquipmentProcessType>): EquipmentProcessType[] {
  const selected = new Set(types);
  const builtIns = EQUIPMENT_PROCESS_TYPES.filter((type) => selected.delete(type));
  return [...builtIns, ...selected];
}

function normalizeList(value: unknown): EquipmentProcessType[] | undefined {
  if (value === undefined) return undefined;
  if (value === null) return [];
  if (!Array.isArray(value)) return undefined;
  return inCanonicalOrder(
    value.map(asProcessType).filter((type): type is EquipmentProcessType => type != null)
  );
}

export function readStockerProcessTypes(source: {
  processTypes?: unknown;
  legacyProcessType?: unknown;
}): EquipmentProcessType[] {
  const explicit = normalizeList(source.processTypes);
  if (explicit) return explicit;
  const legacy = asProcessType(source.legacyProcessType);
  return legacy ? [legacy] : [];
}

/** Value written to the legacy single-value field, or null when set to None. */
export function primaryProcessType(
  types: readonly EquipmentProcessType[]
): EquipmentProcessType | null {
  return types[0] ?? null;
}

export function toggleProcessType(
  types: readonly EquipmentProcessType[],
  processType: EquipmentProcessType
): EquipmentProcessType[] {
  const next = new Set(types);
  if (next.has(processType)) next.delete(processType);
  else next.add(processType);
  return inCanonicalOrder(next);
}

export function formatProcessTypes(types: readonly EquipmentProcessType[]): string {
  return types.length === 0 ? 'None' : types.join(', ');
}
